import React, { useState } from 'react';
import './Insights.css';

function Insights({ transactions, summary }) {
  const [timeframe, setTimeframe] = useState('30'); // days

  // Get recent transactions based on timeframe
  const getRecentTransactions = () => {
    const now = new Date();
    const cutoff = new Date(now.getTime() - (parseInt(timeframe) * 24 * 60 * 60 * 1000));

    return transactions.filter(t => {
      if (!t.Date) return false;
      const parts = t.Date.split('/');
      if (parts.length < 3) return false;
      const txDate = new Date(parts[2], parts[0] - 1, parts[1]);
      return txDate >= cutoff;
    });
  };

  const recentTx = getRecentTransactions();

  // Get biggest expenses (negative amounts, sorted by size)
  const biggestExpenses = recentTx
    .filter(t => {
      const amount = parseFloat((t.Amount || '').replace(/[$,]/g, ''));
      return amount < 0 && !t.IsExcluded;
    })
    .map(t => ({
      ...t,
      parsedAmount: Math.abs(parseFloat((t.Amount || '').replace(/[$,]/g, '')))
    }))
    .sort((a, b) => b.parsedAmount - a.parsedAmount)
    .slice(0, 10);

  // Calculate spending by category for recent period
  const categorySpending = {};
  recentTx.forEach(t => {
    if (t.IsExcluded) return;
    const amount = parseFloat((t.Amount || '').replace(/[$,]/g, ''));
    if (amount < 0) {
      const cat = t.SmartCategory || 'Uncategorized';
      categorySpending[cat] = (categorySpending[cat] || 0) + Math.abs(amount);
    }
  });

  const topCategories = Object.entries(categorySpending)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const totalRecentSpending = Object.values(categorySpending).reduce((a, b) => a + b, 0);

  // Budget targets (could be configurable later)
  const budgetTargets = {
    'Fast Food & Restaurants': 300,
    'Groceries': 600,
    'Amazon/Online Shopping': 200,
    'Entertainment': 100,
    'Gas & Fuel': 150,
    'Gaming': 50,
    'Streaming Services': 50,
  };

  // Calculate budget status
  const budgetStatus = Object.entries(budgetTargets).map(([category, target]) => {
    const spent = categorySpending[category] || 0;
    const percentage = (spent / target) * 100;
    const daysInPeriod = parseInt(timeframe);
    const projectedMonthly = (spent / daysInPeriod) * 30;

    return {
      category,
      target,
      spent,
      percentage,
      remaining: target - spent,
      projectedMonthly,
      status: percentage > 100 ? 'over' : percentage > 80 ? 'warning' : 'good'
    };
  }).filter(b => b.spent > 0 || b.percentage > 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length < 3) return dateStr;
    return `${parts[0]}/${parts[1]}`;
  };

  return (
    <div className="insights">
      <div className="insights-header">
        <h2>Insights</h2>
        <select
          className="timeframe-select"
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value)}
        >
          <option value="7">Last 7 days</option>
          <option value="14">Last 14 days</option>
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
          <option value="90">Last 90 days</option>
          <option value="180">Last 6 months</option>
          <option value="365">Last year</option>
          <option value="730">Last 2 years</option>
          <option value="99999">All time</option>
        </select>
      </div>

      {/* Biggest Recent Expenses */}
      <div className="insights-section">
        <h3>Biggest Expenses</h3>
        <div className="expenses-list">
          {biggestExpenses.length === 0 ? (
            <p className="empty-state">No expenses in this period</p>
          ) : (
            biggestExpenses.map((expense, idx) => (
              <div key={idx} className="expense-item">
                <div className="expense-rank">{idx + 1}</div>
                <div className="expense-details">
                  <span className="expense-desc">{expense.Description}</span>
                  <span className="expense-meta">
                    {formatDate(expense.Date)} · {expense.SmartCategory}
                  </span>
                </div>
                <div className="expense-amount">
                  {formatCurrency(expense.parsedAmount)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Budget Status */}
      <div className="insights-section">
        <h3>Budget Status</h3>
        <p className="section-subtitle">Monthly targets vs current pace</p>
        <div className="budget-list">
          {budgetStatus.length === 0 ? (
            <p className="empty-state">No tracked categories with spending</p>
          ) : (
            budgetStatus.map((budget, idx) => (
              <div key={idx} className={`budget-item ${budget.status}`}>
                <div className="budget-header">
                  <span className="budget-category">{budget.category}</span>
                  <span className="budget-numbers">
                    {formatCurrency(budget.spent)} / {formatCurrency(budget.target)}
                  </span>
                </div>
                <div className="budget-bar-container">
                  <div
                    className={`budget-bar ${budget.status}`}
                    style={{ width: `${Math.min(budget.percentage, 100)}%` }}
                  />
                </div>
                <div className="budget-footer">
                  {budget.status === 'over' ? (
                    <span className="budget-warning">
                      Over by {formatCurrency(Math.abs(budget.remaining))}
                    </span>
                  ) : budget.status === 'warning' ? (
                    <span className="budget-caution">
                      {formatCurrency(budget.remaining)} remaining
                    </span>
                  ) : (
                    <span className="budget-ok">
                      {formatCurrency(budget.remaining)} remaining
                    </span>
                  )}
                  <span className="budget-projected">
                    Projected: {formatCurrency(budget.projectedMonthly)}/mo
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recommendations */}
      {summary?.insights?.recommendations?.length > 0 && (
        <div className="insights-section">
          <h3>Recommendations</h3>
          <div className="recommendations-list">
            {summary.insights.recommendations.map((rec, idx) => (
              <div key={idx} className="recommendation-item">
                <div className="rec-icon">💡</div>
                <div className="rec-content">
                  <span className="rec-title">{rec.title}</span>
                  <span className="rec-message">{rec.message}</span>
                </div>
                {rec.potentialSavings > 0 && (
                  <div className="rec-savings">
                    Save {formatCurrency(rec.potentialSavings)}
                  </div>
                )}
              </div>
            ))}
          </div>
          {summary.insights.totalPotentialSavings > 0 && (
            <div className="total-savings">
              Total potential savings: {formatCurrency(summary.insights.totalPotentialSavings)}
            </div>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="insights-section">
        <h3>Quick Stats</h3>
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-value">{formatCurrency(totalRecentSpending)}</span>
            <span className="stat-label">Spent ({timeframe} days)</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{formatCurrency(totalRecentSpending / parseInt(timeframe))}</span>
            <span className="stat-label">Daily average</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{recentTx.filter(t => !t.IsExcluded && parseFloat((t.Amount || '').replace(/[$,]/g, '')) < 0).length}</span>
            <span className="stat-label">Transactions</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{summary?.savingsRate || 0}%</span>
            <span className="stat-label">Savings rate</span>
          </div>
        </div>
      </div>

      {/* Top Categories */}
      <div className="insights-section">
        <h3>Top Spending Categories</h3>
        <div className="categories-list">
          {topCategories.map(([category, amount], idx) => (
            <div key={idx} className="category-row">
              <span className="category-name">{category}</span>
              <div className="category-bar-wrapper">
                <div
                  className="category-bar"
                  style={{ width: `${(amount / topCategories[0][1]) * 100}%` }}
                />
              </div>
              <span className="category-amount">{formatCurrency(amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Insights;
